import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Calendar } from 'lucide-react';
import { CONTENT_BLOG_POSTS } from '../lib/contentBlog';
import { api } from '../lib/api';
import PageMascots from '../components/PageMascots';

function normalizePost(p) {
  return {
    ...p,
    post_id: p.post_id || p.slug,
    metaTitle: p.metaTitle || p.seo_title || p.meta_title,
    metaDescription: p.metaDescription || p.meta_description,
    cover_image: p.cover_image || p.featured_image_url,
    body: p.body || p.body_markdown || '',
    city: p.city || p.city_tags?.[0] || '',
    bookingUrl: p.bookingUrl || (p.city_tags?.[0] ? `/cities/${String(p.city_tags[0]).toLowerCase().replace(/[^a-z0-9]+/g, '-')}` : ''),
  };
}

export default function Blog() {
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [dbPosts, setDbPosts] = useState([]);
  const perPage = 24;
  const tag = searchParams.get('tag') || '';

  useEffect(() => {
    api.get('/blog').then(r => setDbPosts((r.data || []).map(normalizePost))).catch(() => setDbPosts([]));
  }, []);

  const posts = dbPosts.length ? dbPosts : CONTENT_BLOG_POSTS;
  const tagKeywords = {
    'haunted-spots': ['haunted', 'ghost', 'cemetery', 'hidden folklore'],
    'witchy-corners': ['witch', 'witchy', 'occult', 'magic'],
    'dark-history': ['history', 'monuments', 'myths', 'legends'],
    'weird-cafes': ['cafés', 'cafe', 'coffee'],
    'local-legends': ['legends', 'folklore', 'stories'],
    'tarot-occult': ['tarot', 'occult', 'magic'],
    'mystery-walks': ['mystery', 'mysterious', 'hidden'],
    'photo-spots': ['atmosphere', 'corners', 'streets'],
  };
  const filteredPosts = useMemo(() => tag ? posts.filter(post => {
    const haystack = `${post.title} ${post.excerpt} ${post.body} ${(post.category_tags || []).join(' ')}`.toLowerCase();
    return (tagKeywords[tag] || [tag]).some(keyword => haystack.includes(keyword));
  }) : posts, [posts, tag]);
  const displayPosts = filteredPosts.length ? filteredPosts : posts;
  const totalPages = Math.max(1, Math.ceil(displayPosts.length / perPage));
  const visiblePosts = displayPosts.slice((page - 1) * perPage, page * perPage);

  return (
    <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 overflow-visible">
      <PageMascots />
      <div className="relative z-10">
      <div className="badge-cozy mb-3">Our blog</div>
      <h1 className="text-4xl sm:text-5xl font-heading font-semibold mb-10">Dark travel tips</h1>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {visiblePosts.map(p => (
          <Link key={p.post_id} to={`/blog/${p.slug}`} className="magical-card overflow-hidden" data-testid={`blog-card-${p.slug}`}>
            {p.cover_image && <img src={p.cover_image} alt={p.title} className="w-full h-48 object-cover"/>}
            <div className="p-5">
              <h3 className="font-heading text-xl mb-2 leading-snug">{p.title}</h3>
              <p className="text-sm text-[#a3a3a3] line-clamp-3">{p.excerpt}</p>
              <div className="mt-3 text-xs text-[#a3a3a3] flex items-center gap-1"><Calendar size={11}/> {p.published_at ? new Date(p.published_at).toLocaleDateString() : 'Ghostie journal'}</div>
            </div>
          </Link>
        ))}
      </div>
      {totalPages > 1 && (
        <div className="flex justify-center flex-wrap gap-2 mt-10">
          {Array.from({ length: totalPages }, (_, index) => index + 1).map(n => (
            <button key={n} onClick={()=>setPage(n)} className={`chip ${page === n ? 'active' : ''}`}>
              {n}
            </button>
          ))}
        </div>
      )}
      </div>
    </div>
  );
}
